import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Loader2, ArrowLeft, Info } from "lucide-react";
import { MatchRow } from "@/components/football/MatchRow";
import { endpoints } from "@/lib/api";

const TABS = [["standings", "Standings"], ["fixtures", "Fixtures"], ["results", "Results"], ["scorers", "Top Scorers"], ["teams", "Teams"]];
const FORM = { W: "bg-emerald-500", D: "bg-slate-500", L: "bg-red-500" };

export default function FootballCompetition() {
  const { slug } = useParams();
  const [comp, setComp] = useState(null);
  const [tab, setTab] = useState("standings");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => {
    endpoints.footballCompetition(slug).then((r) => setComp(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!comp) return;
    setTabLoading(true);
    const fn = tab === "standings" ? endpoints.footballStandings
      : tab === "fixtures" ? endpoints.footballFixtures
      : tab === "scorers" ? endpoints.footballScorers
      : tab === "teams" ? endpoints.footballCompTeams
      : endpoints.footballResults;
    fn(slug).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setTabLoading(false));
  }, [tab, comp, slug]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!comp) return <div className="text-center py-32 text-slate-400">Competition not found.</div>;

  const isSportmonks = comp.provider === "sportmonks";
  const Logo = ({ team, size = 20 }) => (
    team?.logoUrl ? <img src={team.logoUrl} alt="" style={{ width: size, height: size }} className="object-contain inline-block" /> : null
  );

  const Awaiting = () => (
    <div className="rounded-xl bg-[#121620] border border-[#273042] p-8 text-center" data-testid="awaiting-provider">
      <Info className="w-8 h-8 text-amber-400 mx-auto mb-3" />
      <p className="text-white font-semibold">Live data awaiting provider configuration</p>
      <p className="text-slate-400 text-sm mt-1">{data?.message || "This competition will show live standings, fixtures and results once the data provider is connected."}</p>
    </div>
  );

  const canOpen = (f) => isSportmonks && f.id != null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="competition-page">
      <Link to="/football" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-5"><ArrowLeft className="w-4 h-4" /> All competitions</Link>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#121620] border border-[#273042] flex items-center justify-center shrink-0 overflow-hidden">
          {comp.logoUrl ? <img src={comp.logoUrl} alt="" className="w-10 h-10 object-contain" /> : <Trophy className="w-7 h-7 text-[#E11D2A]" />}
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">{comp.name}</h1>
          <p className="text-slate-500 text-sm">{comp.country}{comp.currentSeason ? ` · ${comp.currentSeason.name}` : ""}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#273042] overflow-x-auto">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === k ? "text-white border-[#E11D2A]" : "text-slate-400 border-transparent hover:text-white"}`}>{label}</button>
        ))}
      </div>

      {tabLoading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>
        : data?.awaitingProvider ? <Awaiting />
        : tab === "standings" ? (
          (data?.standings?.length || 0) === 0 ? <p className="text-slate-500 text-center py-12" data-testid="standings-empty">No standings yet.</p> : (
            <div className="overflow-x-auto rounded-xl border border-[#273042]" data-testid="standings-table">
              <table className="w-full text-sm">
                <thead className="bg-[#121620] text-slate-400">
                  <tr><th className="text-left py-3 px-3">#</th><th className="text-left py-3 px-2">Team</th>
                    <th className="py-3 px-2">P</th><th className="py-3 px-2">W</th><th className="py-3 px-2">D</th><th className="py-3 px-2">L</th>
                    <th className="py-3 px-2 hidden sm:table-cell">GF</th><th className="py-3 px-2 hidden sm:table-cell">GA</th><th className="py-3 px-2">GD</th><th className="py-3 px-3 font-bold text-white">Pts</th><th className="py-3 px-2 hidden md:table-cell">Form</th></tr>
                </thead>
                <tbody>
                  {data.standings.map((r, i) => (
                    <tr key={i} className="border-t border-[#273042] text-slate-200">
                      <td className="py-2.5 px-3 text-slate-500">{r.rank}</td>
                      <td className="py-2.5 px-2">{r.team?.id ? <Link to={`/football/team/${r.team.id}`} className="flex items-center gap-2 hover:text-white" data-testid={`standing-team-${r.team.id}`}><Logo team={r.team} /> <span className="font-medium">{r.team?.name}</span></Link> : <span className="flex items-center gap-2"><Logo team={r.team} /> <span className="font-medium">{r.team?.name}</span></span>}</td>
                      <td className="text-center">{r.played}</td><td className="text-center">{r.won}</td><td className="text-center">{r.drawn}</td><td className="text-center">{r.lost}</td>
                      <td className="text-center hidden sm:table-cell">{r.gf}</td><td className="text-center hidden sm:table-cell">{r.ga}</td><td className="text-center">{r.gd}</td>
                      <td className="text-center px-3 font-extrabold text-white">{r.points}</td>
                      <td className="hidden md:table-cell px-2"><span className="flex gap-1">{(r.form || []).map((f, k) => <span key={k} className={`w-5 h-5 rounded text-[10px] font-bold text-white flex items-center justify-center ${FORM[f] || "bg-slate-700"}`}>{f}</span>)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : tab === "scorers" ? (
          (data?.scorers?.length || 0) === 0 ? <p className="text-slate-500 text-center py-12" data-testid="scorers-empty">Top scorers are not available for this competition.</p> : (
            <div className="rounded-xl border border-[#273042] overflow-hidden" data-testid="scorers-list">
              {data.scorers.map((s, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-[#273042] first:border-t-0">
                  <span className={`w-7 text-center font-bold ${s.position <= 3 ? "text-[#E11D2A]" : "text-slate-500"}`}>{s.position}</span>
                  {s.photoUrl ? <img src={s.photoUrl} alt="" className="w-9 h-9 rounded-full object-cover bg-[#0A0C10]" /> : <span className="w-9 h-9 rounded-full bg-[#0A0C10] border border-[#273042]" />}
                  <div className="min-w-0 flex-1">
                    {s.playerId ? <Link to={`/football/player/${s.playerId}`} className="text-white font-semibold truncate block hover:text-[#EF4444]">{s.player}</Link> : <p className="text-white font-semibold truncate">{s.player}</p>}
                    <p className="text-xs text-slate-500 truncate">{s.team}</p>
                  </div>
                  <span className="text-white font-extrabold text-lg">{s.goals}</span>
                  <span className="text-[11px] text-slate-500">goals</span>
                </div>
              ))}
            </div>
          )
        ) : tab === "teams" ? (
          (data?.teams?.length || 0) === 0 ? <p className="text-slate-500 text-center py-12" data-testid="teams-empty">Teams are not available for this competition.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="teams-list">
              {data.teams.map((t) => (
                <Link key={t.id} to={`/football/team/${t.id}`} data-testid={`team-card-${t.id}`} className="rounded-xl bg-[#121620] border border-[#273042] p-4 flex items-center gap-3 hover:border-[#E11D2A]/60 transition-colors">
                  <Logo team={t} /><span className="text-white font-medium truncate">{t.name}</span>
                </Link>
              ))}
            </div>
          )
        ) : (
          (data?.fixtures?.length || 0) === 0 ? <p className="text-slate-500 text-center py-12" data-testid={`${tab}-empty`}>No {tab} yet.</p> : (
            <div className="space-y-2" data-testid={`${tab}-list`}>
              {data.fixtures.map((f, i) => (canOpen(f) ? <MatchRow key={f.id} f={f} showDate /> : (
                <div key={f.id || i} className="rounded-xl bg-[#121620] border border-[#273042] p-3.5 flex items-center justify-between gap-2">
                  <span className="flex-1 flex items-center justify-end gap-2 text-right text-white font-medium min-w-0"><span className="truncate">{f.home?.name}</span> <Logo team={f.home} /></span>
                  <span className="px-3 shrink-0 text-center">{f.status === "finished" ? <span className="font-extrabold text-white">{f.homeScore ?? "-"} : {f.awayScore ?? "-"}</span>
                    : <span className="text-xs text-slate-400">{f.date ? new Date(f.date).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "TBD"}</span>}</span>
                  <span className="flex-1 flex items-center gap-2 text-left text-white font-medium min-w-0"><Logo team={f.away} /> <span className="truncate">{f.away?.name}</span></span>
                </div>
              )))}
            </div>
          )
        )}

    </div>
  );
}
